import JsonLd from '@/components/base/JsonLd';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function QualityGuide() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://allchinabuyspreadsheet.wiki';
  const pagePath = '/allchinabuy-sheet-quality';
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'AllChinaBuySpreadsheet Quality Control: How Every Product Gets Verified',
    description: 'Deep dive into the three-tier quality rating system used by AllChinaBuySpreadsheet. Learn how Standard, Premium, and Elite products are verified through material checks, construction assessment, and physical inspection.',
    author: { '@type': 'Organization', name: 'AllChinaBuySpreadsheet' },
    publisher: { '@type': 'Organization', name: 'AllChinaBuySpreadsheet', url: siteUrl },
    datePublished: '2026-06-25',
    dateModified: '2026-06-25',
    mainEntityOfPage: { '@type': 'WebPage', '@id': siteUrl + pagePath },
    url: siteUrl + pagePath,
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'AllChinaBuySpreadsheet Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Quality Control', item: siteUrl + pagePath },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />
      <Navbar />
      <header className="relative h-[420px] flex items-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Quality%20inspector%20examining%20fabric%20with%20magnifying%20tool%20in%20bright%20workshop%2C%20quality%20control%20concept%2C%20professional%20inspection%20photography%2C%20clean%20industrial%20setting%2C%20detailed%20textile%20examination%2C%20precision%20and%20care%20theme%2C%20modern%20manufacturing&width=1800&height=840&seq=quality-hero-v3&orientation=landscape"
          alt="AllChinaBuySpreadsheet quality control"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/50"></div>
        <div className="relative z-10 w-full px-6 md:px-10 pt-16">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block px-3 py-1 bg-white/15 backdrop-blur-sm text-xs font-medium text-white rounded-full uppercase tracking-[0.15em] mb-5">Quality</span>
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-white leading-tight">AllChinaBuySpreadsheet Quality Control: How Every Product Gets Verified</h1>
          </div>
        </div>
      </header>
      <article className="py-16 md:py-24 px-6 md:px-10">
        <div className="max-w-3xl mx-auto">
          <p className="text-lg text-foreground-700 leading-relaxed mb-8">
            Quality inconsistency is the single biggest complaint among international buyers of Chinese products. The
            <strong className="text-primary-600 font-semibold"> AllChinaBuySpreadsheet</strong> quality verification
            system was built specifically to address this problem — ensuring that every product in our spreadsheets
            meets a consistent quality baseline before it ever reaches your screen.
          </p>
          <h2 className="text-2xl font-heading font-bold text-foreground-950 mt-14 mb-5">The Three-Tier Quality Rating System</h2>
          <p className="text-base text-foreground-700 leading-relaxed mb-5">
            Products in our <strong className="text-primary-600 font-semibold">china product spreadsheet list</strong> receive
            one of three quality ratings: Standard (meets basic quality expectations), Premium (exceeds industry standards
            for materials and construction), and Elite (top-tier quality verified through physical inspection). Each tier
            has specific, objective criteria that must be met, eliminating subjective "5-star" rating inflation.
          </p>
          <p className="text-base text-foreground-700 leading-relaxed mb-5">
            The <strong className="text-primary-600 font-semibold">all chinabuy spreadsheet</strong> quality data includes
            material composition verification, stitching and construction assessment for apparel categories, and
            durability testing results where applicable. This level of detail simply does not exist on traditional
            marketplace platforms where quality information is limited to user reviews of unknown reliability.
          </p>
          <h2 className="text-2xl font-heading font-bold text-foreground-950 mt-14 mb-5">Physical Inspection for Elite Tier</h2>
          <p className="text-base text-foreground-700 leading-relaxed mb-5">
            Elite-tier products undergo physical inspection by our verification team. Samples are ordered, examined, and
            compared against listing descriptions. Only products that match or exceed their described specifications earn
            the Elite designation. The <strong className="text-primary-600 font-semibold">best china spreadsheet products</strong>
            in this tier represent the top 5% of all listings and are the safest choices for buyers who prioritize quality
            above all else.
          </p>
          <div className="mt-14 p-8 bg-primary-50 rounded-2xl text-center">
            <h3 className="text-xl font-heading font-bold text-foreground-950 mb-3">Browse Verified Quality Products</h3>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-950 text-background-50 text-sm font-medium rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer">
                Shop Quality Products <i className="ri-arrow-right-line"></i>
              </a>
              <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 border border-foreground-200 text-sm font-medium text-foreground-700 rounded-full hover:bg-foreground-50 transition-colors whitespace-nowrap cursor-pointer">
                Back to Home <i className="ri-home-4-line"></i>
              </Link>
            </div>
          </div>
          <nav className="mt-12 pt-8 border-t border-background-200/70">
            <p className="text-xs font-medium text-foreground-500 uppercase tracking-wider mb-4">Related Articles</p>
            <ul className="space-y-2">
              <li><Link to="/allchinabuy-sheet-safety" className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">Safety Guide: Full Verification Process &rarr;</Link></li>
              <li><Link to="/allchinabuy-sheet-vs-competitors" className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">Competitor Comparison: Full Breakdown &rarr;</Link></li>
              <li><Link to="/" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors cursor-pointer">&larr; AllChinaBuySpreadsheet Home</Link></li>
            </ul>
          </nav>
        </div>
      </article>
      <Footer />
    </div>
  );
}